// @todo: Migrate to 'Dialog.Drawer' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import Drawer from '@material-ui/core/Drawer';
import {Button} from '@wandb/components';
// @todo: Migrate to 'TextField' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {TextField} from '@wandb/weave/components/Form/TextField';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {FC, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {useGenerateApiKeyMutation} from '../generated/graphql';
import {GenerateApiKeyMutationData} from '../graphql/users';
import {useHandleScroll} from '../pages/HomePage/HomePageSidebar/useHandleScroll';
import {DateFormat, format} from '../util/date';
import {extractErrorMessageFromApolloError} from '../util/errors';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from './Banner';
import {ResizableDrawer} from './common/ResizableDrawer/ResizableDrawer';

export const DEFAULT_API_KEY_DESCRIPTION = 'Untitled Key';

type CreateApiKeyDrawerProps = {
  isOpen: boolean;
  close: () => void;
  refetch: () => void;
};

enum Steps {
  CREATE_API_KEY,
  CREATE_API_KEY_CONFIRMATION,
}

export const CreateApiKeyDrawer: FC<CreateApiKeyDrawerProps> = ({
  isOpen,
  close,
  refetch,
}) => {
  const scrolled = useHandleScroll();
  const [width, setWidth] = useState(480);
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };
  const onClose = () => {
    setDescription(DEFAULT_API_KEY_DESCRIPTION);
    setGenerateApiKeyData(null);
    setStep(Steps.CREATE_API_KEY);
    close();
  };

  const [step, setStep] = useState(Steps.CREATE_API_KEY);
  const [description, setDescription] = useState<string>(
    DEFAULT_API_KEY_DESCRIPTION
  );
  const [generateApiKeyData, setGenerateApiKeyData] = useState<any>(null);
  const [generateApiKeyMutation] = useGenerateApiKeyMutation();

  const createApiKey = async () => {
    try {
      const result = await generateApiKeyMutation({
        variables: {
          description: description,
        },
      });

      setGenerateApiKeyData(result.data);
      setStep(Steps.CREATE_API_KEY_CONFIRMATION);
      refetch();
    } catch (err) {
      toast(extractErrorMessageFromApolloError(err));
    }
  };

  const padding = `${Math.max(60 - scrolled.scrollHeight, 0)}px 20px 20px 20px`;

  return (
    <Drawer anchor="right" open={isOpen} onClose={close}>
      <ResizableDrawer
        startWidth={width}
        minWidth={400}
        onSetWidth={onSetWidth}>
        <Tailwind>
          <div className="flex h-screen flex-col" style={{padding: padding}}>
            <div className="flex h-60 items-center flex-shrink-0">
              <div className="font-source-sans-pro text-xl font-semibold leading-10">
                Create new API key
              </div>
              <div className="flex-shrink flex-grow basis-auto text-right">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded"
                  type="button">
                  <Icon name="close" size="large" />
                </button>
              </div>
            </div>
            <div className="-ml-20 -mr-20 h-px bg-moon-250 flex-shrink-0" />
            <div className="flex-1 flex flex-col min-h-0">
              {step === Steps.CREATE_API_KEY && (
                <CreateApiKeyDrawerForm
                  description={description}
                  setDescription={setDescription}
                  createApiKey={createApiKey}
                  close={onClose}
                />
              )}
              {step === Steps.CREATE_API_KEY_CONFIRMATION && (
                <CreateApiKeyDrawerConfirmation
                  generateApiKeyData={generateApiKeyData}
                  close={onClose}
                />
              )}
            </div>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};

const CreateApiKeyDrawerForm: FC<{
  description: string;
  setDescription: (description: string) => void;
  createApiKey: () => void;
  close: () => void;
}> = ({description, setDescription, createApiKey, close}) => {
  return (
    <div className="flex flex-col h-full gap-16 pt-16">
      <div className="text-lg font-semibold text-moon-600">
        Step 1 of 2 &mdash; Configure key
      </div>
      <Banner variant="info">
        <BannerFlexWrapper>
          <BannerTextWrapper>
            <Icon name="lightbulb-info" />
            {/* TODO(jennwandb:hashed-api-keys): WB-26650 update for service account api key creation */}
            <div className="pl-8">
              This key will be only applicable to{' '}
              <span className="font-semibold">Personal account</span>
            </div>
          </BannerTextWrapper>
        </BannerFlexWrapper>
      </Banner>
      <div className="flex flex-col flex-1 gap-8">
        <div className="text-base font-semibold text-primary">
          Name{' '}
          <span className="text-moon-500 text-sm font-normal">(optional)</span>
        </div>
        <TextField
          value={description}
          dataTest="api-key-description-input"
          onChange={setDescription}
          maxLength={40}
        />
      </div>
      <div className="flex flex-col gap-16">
        <div className="-ml-20 -mr-20 h-px bg-moon-250 flex-shrink-0" />
        <div className="flex gap-16">
          <div className="flex-1">
            <Button
              fullWidth
              data-test="cancel-create-api-key-button"
              variant="secondary"
              size="md"
              onPress={close}>
              Cancel
            </Button>
          </div>
          <div className="flex-1">
            <Button
              fullWidth
              data-test="create-api-key-button"
              variant="primary"
              size="md"
              onPress={createApiKey}>
              Create API key
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateApiKeyDrawerConfirmation: FC<{
  generateApiKeyData: GenerateApiKeyMutationData;
  close: () => void;
}> = ({generateApiKeyData, close}) => {
  const apiKeyData = generateApiKeyData?.generateApiKey;
  return (
    <div className="flex flex-col h-full">
      <div className="h-full py-16">
        <div className="text-lg font-semibold text-moon-600 pb-16">
          Step 2 of 2 &mdash; Save key
        </div>
        <NewApiKeyConfirmation generateApiKeyData={generateApiKeyData} />
      </div>
      <div className="flex flex-col gap-16">
        <div className="-ml-20 -mr-20 h-px bg-moon-250 flex-shrink-0" />
        <Button fullWidth variant="primary" size="md" onPress={close}>
          Done
        </Button>
      </div>
    </div>
  );
};

export const NewApiKeyConfirmation: FC<{
  generateApiKeyData: GenerateApiKeyMutationData;
}> = ({generateApiKeyData}) => {
  const apiKeyData = generateApiKeyData?.generateApiKey;
  const copyApiKey = () => {
    if (apiKeyData?.secretApiKey) {
      navigator.clipboard.writeText(apiKeyData.secretApiKey);
      toast.success('API key copied to clipboard');
    }
  };
  return (
    <div className="flex flex-col gap-16">
      <Banner variant="warning">
        <BannerFlexWrapper>
          <BannerTextWrapper className="flex gap-16">
            <div className="flex items-center">
              <Icon name="warning" />
            </div>
            <div>
              <span className="font-semibold">SAVE THIS KEY NOW.</span> This is
              the only time you&apos;ll see the complete key.
            </div>
          </BannerTextWrapper>
        </BannerFlexWrapper>
      </Banner>
      <div className="flex flex-col flex-1 gap-8">
        <div className="rounded-lg p-12 border border-moon-300">
          <div className="bg-moon-100 rounded-lg px-16 py-8 text-sm">
            <div className="flex w-full justify-between">
              <div className="font-semibold font-['Inconsolata']">API KEY</div>
              <div
                onClick={copyApiKey}
                className="flex gap-4 bg-moon-150 rounded-md py-2 px-8 hover:cursor-pointer hover:bg-moon-200">
                <Icon className="copy-icon" name="copy" />
                <div className="font-semibold text-moon-600">Copy</div>
              </div>
            </div>
            <div className="overflow-wrap-anywhere w-full break-words font-['Inconsolata'] py-8">
              {apiKeyData?.secretApiKey
                ? apiKeyData.secretApiKey
                : 'No API key returned, try again'}
            </div>
          </div>
          <div className="p-16 text-sm">
            <div>Key information</div>
            <div className="flex gap-4 flex-wrap">
              <div className="text-moon-500 min-w-[80px]">Key ID</div>
              <div className="text-moon-600">
                : {apiKeyData?.apiKey?.apiKeyId && apiKeyData.apiKey.apiKeyId}
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-moon-500 min-w-[80px]">Name</div>
              <div className="text-moon-600">
                :{' '}
                {apiKeyData?.apiKey?.description &&
                  apiKeyData.apiKey.description}
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-moon-500 min-w-[80px]">Created</div>
              <div className="text-moon-600">
                :{' '}
                {apiKeyData?.apiKey?.createdAt &&
                  format(
                    new Date(apiKeyData?.apiKey?.createdAt),
                    DateFormat.MONTH_DAY_YEAR
                  )}
              </div>
            </div>
            <div className="flex gap-4 flex-wrap">
              <div className="text-moon-500 min-w-[80px]">Owner</div>
              <div className="text-moon-600">
                :{' '}
                {apiKeyData?.apiKey?.user?.name && apiKeyData.apiKey.user.name}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
